import React from "react";
import { useContent } from "@thoughtbot/superglue";

import {
  Form,
  type FormProps,
  type RailsHiddenFieldProps,
  type RailsPasswordFieldProps,
  type RailsSubmitButtonProps,
} from "@javascript/components/Inputs";
import { AuthCard } from "@javascript/components/AuthCard";
import { AuthShell } from "@javascript/components/AuthShell";
import { Button } from "@javascript/components/ui/button";
import { Field, FieldError, FieldLabel } from "@javascript/components/ui/field";
import { Input } from "@javascript/components/ui/input";
import { TypographyH1, TypographyMuted } from "@javascript/components/ui/typography";

type PasswordResetContent = {
  passwordForm: FormProps<{
    resetPasswordToken: RailsHiddenFieldProps;
    password: RailsPasswordFieldProps;
    passwordConfirmation: RailsPasswordFieldProps;
    submit: RailsSubmitButtonProps;
  }>;
  errors: Record<string, string[]>;
  signInPath: string;
  brandMotifPath: string;
};

export default function PasswordReset() {
  const { passwordForm, errors, signInPath, brandMotifPath } = useContent<PasswordResetContent>();
  const { form, extras, inputs } = passwordForm;
  const resetTokenErrors = (errors.reset_password_token ?? []).map((message) => ({ message }));
  const passwordErrors = (errors.password ?? []).map((message) => ({ message }));
  const passwordConfirmationErrors = (errors.password_confirmation ?? []).map((message) => ({ message }));

  return (
    <AuthShell brandMotifPath={brandMotifPath}>
      <AuthCard>
        <div className="space-y-2">
          <TypographyH1 className="text-left">Choose a new password</TypographyH1>
          <TypographyMuted>Your new password must be at least six characters.</TypographyMuted>
        </div>

        <FieldError className="mt-6" errors={resetTokenErrors} />

        <Form {...form} extras={extras} className="mt-6 space-y-5">
          <input {...inputs.resetPasswordToken} />
          <Field data-invalid={passwordErrors.length > 0}>
            <FieldLabel htmlFor={inputs.password.id}>New password</FieldLabel>
            <Input {...inputs.password} aria-invalid={passwordErrors.length > 0} />
            <FieldError errors={passwordErrors} />
          </Field>
          <Field data-invalid={passwordConfirmationErrors.length > 0}>
            <FieldLabel htmlFor={inputs.passwordConfirmation.id}>Confirm new password</FieldLabel>
            <Input {...inputs.passwordConfirmation} aria-invalid={passwordConfirmationErrors.length > 0} />
            <FieldError errors={passwordConfirmationErrors} />
          </Field>
          <Button type="submit" className="h-12 w-full rounded-full text-base font-semibold shadow-sm">
            {inputs.submit.text}
          </Button>
        </Form>
      </AuthCard>

      {/* Outside the card on purpose: the card is the task, and this speaks
          about the account around it. */}
      <p
        data-auth-signin-prompt
        className="mt-5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-sm"
      >
        <span className="text-muted-foreground">Remember your password?</span>
        <a
          href={signInPath}
          className="inline-flex min-h-11 items-center gap-1.5 whitespace-nowrap font-semibold text-foreground underline-offset-4 transition-colors hover:text-primary hover:underline focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
        >
          Sign in
          <span aria-hidden className="text-primary">
            →
          </span>
        </a>
      </p>
    </AuthShell>
  );
}
